import React from "react";
import cns from "classnames";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BackIcon from "react-icons/lib/md/arrow-back";
import CloseIcon from "react-icons/lib/md/close";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { ThemeType } from "@theme";
import { getParentRoute, getRootRoute } from "@utils/navigationUtils";
import { useLegacyNavigation } from "@navigation";

import { RegularButton } from "./buttons/RegularButton";
import { PlainLink } from "./typography/PlainLink";

export type RenderChildrenProps = {
  closeBtn: React.ReactNode;
  backBtn: React.ReactNode;
};

export type TopBarProps = {
  className?: string;
  children?: React.ReactNode | ((props: RenderChildrenProps) => React.ReactNode);
  onBack?: () => void;
  rightLink?: string;
  rightLinkText?: string;
};

/**
 * This a top component for item pages
 */
export function Topbar(props: TopBarProps) {
  const classes = useStyles(props);
  const { children, onBack: propsBack } = props;
  let close;
  let canBack;
  let onBack;
  const navigation = useLegacyNavigation();

  if (navigation) {
    const { canGoBack, goBack } = navigation;

    close = () =>
      propsBack ? propsBack() : store().navigation.clearInstance(navigation.name);
    canBack = canGoBack;
    onBack = () => goBack();
  } else {
    const { history } = store().routing;
    const { pathname } = history.location;

    canBack = window.history.length > 0;
    close = () => history.push(getRootRoute(pathname));
    onBack = () => history.push(getParentRoute(pathname));
  }

  const closeBtn = (
    <Button className={classes.closeButton} onClick={close} data-test="CloseButton">
      <CloseIcon size="24" />
    </Button>
  );

  const backBtn = canBack ? (
    <RegularButton
      aria-label={locale.Back}
      onClick={propsBack || onBack}
      data-test="BackButton"
      leftIcon={<BackIcon fontSize="small" />}
      variant="text"
    >
      {locale.Back}
    </RegularButton>
  ) : null;

  const rightLink = props.rightLink ? (
    <PlainLink target="_blank" href={props.rightLink} style={{ marginLeft: "auto" }}>
      {props.rightLinkText}
    </PlainLink>
  ) : null;

  const extra = typeof children !== "function" ? children : null;

  const innerContent =
    typeof children === "function" ? (
      children({ backBtn, closeBtn })
    ) : (
      <>
        {closeBtn} {backBtn} {extra} {rightLink}
      </>
    );

  return (
    <div className={cns(classes.root, props.className)} data-test="Topbar">
      {innerContent}
    </div>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginRight(8),
    alignItems: "center",
    display: "flex"
  },

  closeButton: {
    minWidth: 40,
    display: "flex"
  },

  buttonText: {
    marginLeft: theme.spacing(),
    fontWeight: "bold",
    textTransform: "none"
  },

  topIcon: {
    transform: "rotate(90deg)"
  }
}));
